// Popover (anchored floating panel) and Menu (popover list of actions / choices). DESIGN §2.
// Popovers are position: fixed next to their anchor, flip above when there is no room below, close on outside
// pointer-down and Esc (focus returns to the anchor), and follow the anchor on scroll/resize.
import './popover.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../icons.jsx';
import { cx } from './ui.jsx';

const GAP = 6;
const EDGE = 8;

function place(anchor, pop, placement) {
  if (!anchor || !pop) return;
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = pop.offsetWidth;
  const h = pop.offsetHeight;
  const [side, align] = placement.split('-');
  let top = side === 'top' ? r.top - GAP - h : r.bottom + GAP;
  if (side !== 'top' && top + h > vh - EDGE && r.top - GAP - h >= EDGE) top = r.top - GAP - h;
  if (side === 'top' && top < EDGE) top = r.bottom + GAP;
  let left = align === 'end' ? r.right - w : align === 'center' ? r.left + r.width / 2 - w / 2 : r.left;
  left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
  top = Math.max(EDGE, Math.min(top, vh - h - EDGE));
  pop.style.top = `${Math.round(top)}px`;
  pop.style.left = `${Math.round(left)}px`;
}

// Open/close state + anchor ref for a trigger element: const pop = usePopover(); <button ref={pop.anchorRef} {...pop.triggerProps}>
export function usePopover(initial = false) {
  const [open, setOpen] = useState(initial);
  const anchorRef = useRef(null);
  return {
    open,
    setOpen,
    anchorRef,
    close: () => setOpen(false),
    toggle: () => setOpen((o) => !o),
    triggerProps: { onClick: () => setOpen((o) => !o), 'aria-expanded': open ? 'true' : 'false', 'aria-haspopup': 'true' },
  };
}

// placement: 'bottom-start' | 'bottom-end' (default) | 'bottom-center' | 'top-start' | 'top-end'.
// width: px number (clamped to the viewport) or undefined for content width. role: 'dialog' (default) | 'menu'.
export function Popover({ open, onClose, anchorRef, placement = 'bottom-end', width, label, role = 'dialog', class: cls, autoFocus = true, children }) {
  const popRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (!open) return undefined;
    const pop = popRef.current;
    const reposition = () => place(anchorRef?.current, pop, placement);
    reposition();
    if (autoFocus) {
      const first = pop.querySelector('[data-autofocus], [role="menuitem"]:not([disabled]), input, button:not([disabled]), a[href]');
      (first || pop).focus({ preventScroll: true });
    }
    const onDown = (e) => {
      if (pop.contains(e.target) || anchorRef?.current?.contains(e.target)) return;
      closeRef.current?.();
    };
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      closeRef.current?.();
      anchorRef?.current?.focus?.({ preventScroll: true });
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reposition) : null;
    ro?.observe(pop);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      ro?.disconnect();
    };
  }, [open, placement]);

  if (!open) return null;
  return (
    <div
      ref={popRef}
      class={cx('popover', cls)}
      role={role}
      aria-label={label}
      tabIndex={-1}
      style={{ width: width ? `min(${width}px, calc(100vw - ${EDGE * 2}px))` : undefined }}
    >
      {children}
    </div>
  );
}

function menuKeys(e) {
  const items = [...e.currentTarget.querySelectorAll('[role="menuitem"]:not([disabled]), [role="menuitemradio"]:not([disabled])')];
  const i = items.indexOf(document.activeElement);
  const next = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: items.length - 1 }[e.key];
  if (next === undefined || !items.length) return;
  e.preventDefault();
  items[(next + items.length) % items.length].focus();
}

// A list of actions or choices in a Popover.
// items: [{ label, icon?, avatar?, hint?, onSelect?(), href?, danger?, checked?, disabled?, meta?, keepOpen?, class? } | { divider: true } | { heading: 'LABEL' }]
// `checked` (boolean) turns the item into a radio-style choice with a ✓. Selecting an item closes the menu.
export function MenuList({ items = [], onClose }) {
  return (
    <div class="menu" onKeyDown={menuKeys}>
      {items.map((item, i) => {
        if (!item) return null;
        if (item.divider) return <div key={i} class={cx('menu-divider', item.class)} role="separator" />;
        if (item.heading) return <div key={i} class="menu-heading">{item.heading}</div>;
        const choice = typeof item.checked === 'boolean';
        const props = {
          class: cx('menu-item', item.danger && 'is-danger', item.checked && 'is-checked', item.class),
          role: choice ? 'menuitemradio' : 'menuitem',
          'aria-checked': choice ? String(item.checked) : undefined,
          tabIndex: -1,
          onClick: () => { if (!item.keepOpen) onClose?.(); item.onSelect?.(); },
        };
        const body = (
          <>
            {item.icon && <Icon name={item.icon} size={16} class="menu-icon" />}
            {item.avatar}
            <span class="menu-label">{item.label}</span>
            {item.meta && <span class="menu-meta">{item.meta}</span>}
            {item.hint && <span class="kbd">{item.hint}</span>}
            {choice && <Icon name="check" size={14} class="menu-check" />}
          </>
        );
        if (item.href && !item.disabled) return <a key={i} {...props} href={item.href}>{body}</a>;
        return <button key={i} type="button" disabled={item.disabled} {...props}>{body}</button>;
      })}
    </div>
  );
}

// Trigger + menu. `trigger(props)` renders the trigger element and must spread `props` (ref, onClick, aria-*) on a
// native element: <Menu items={…} trigger={(p) => <button class="icon-btn" {...p}>…</button>} />.
export function Menu({ items, trigger, placement = 'bottom-end', width = 220, label = 'Menu', header, class: cls }) {
  const pop = usePopover();
  return (
    <>
      {trigger({ ref: pop.anchorRef, ...pop.triggerProps })}
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} placement={placement} width={width} label={label} role="menu" class={cx('popover-menu', cls)}>
        {header}
        <MenuList items={items} onClose={pop.close} />
      </Popover>
    </>
  );
}
